import { HumanTestVerdicts } from "@/components/walkthrough/human-test-verdicts";
import { ReasoningText } from "@/components/walkthrough/reasoning-text";
import { SinceLastRun } from "@/components/walkthrough/since-last-run";
import { VerificationOutcomesList } from "@/components/walkthrough/verification-outcomes-list";
import type { RunVerification } from "@/lib/run-findings";
import type { WalkthroughFinding, FindingKind } from "./workspace-types";
import { WorkspaceFindingsToolbar } from "./workspace-findings-toolbar";
import { WorkspaceFindingsList } from "./workspace-findings-list";
import {
  ZeroFindingsState,
  FilteredFindingsEmptyState,
} from "./workspace-findings-empty-states";
import { DesignerReviewSection } from "./designer-review-section";

interface WorkspaceFindingsTabProps {
  verdicts?: Array<{ question: string; rating: string; reasoning?: string }>;
  findingTitles: Map<string, string>;
  isVerification: boolean;
  walkthrough: any;
  walkthroughSteps: Array<{ id: string; stepNumber: number }>;
  allFindings: WalkthroughFinding[];
  filteredFindings: WalkthroughFinding[];
  problemsCount: number;
  helpsCount: number;
  openCount: number;
  kindFilter: FindingKind;
  onKindFilterChange: (kind: FindingKind) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  severityFilter: string;
  onSeverityFilterChange: (severity: string) => void;
  personaName?: string | null;
  projectId: string;
  canEdit: boolean;
  hasLinkedRepo: boolean;
  selectedIds: Set<string>;
  focusedIndex: number;
  allFilteredSelected: boolean;
  someFilteredSelected: boolean;
  onToggleSelectAll: () => void;
  onToggleSelect: (id: string, checked: boolean) => void;
  onOpenFinding: (id: string) => void;
  onSelectDeltaFinding: (id: string, item: any) => void;
  onOpenZenMode?: (index?: number) => void;
}

export function WorkspaceFindingsTab({
  verdicts,
  findingTitles,
  isVerification,
  walkthrough,
  walkthroughSteps,
  allFindings,
  filteredFindings,
  problemsCount,
  helpsCount,
  openCount,
  kindFilter,
  onKindFilterChange,
  statusFilter,
  onStatusFilterChange,
  severityFilter,
  onSeverityFilterChange,
  personaName,
  projectId,
  canEdit,
  hasLinkedRepo,
  selectedIds,
  focusedIndex,
  allFilteredSelected,
  someFilteredSelected,
  onToggleSelectAll,
  onToggleSelect,
  onOpenFinding,
  onSelectDeltaFinding,
  onOpenZenMode,
}: WorkspaceFindingsTabProps) {
  const isComplete = walkthrough.status === "completed";

  const hasVerdicts = Boolean(verdicts && verdicts.length > 0);
  const hasDesignerReview = Boolean(
    !isVerification && walkthrough.designerReview,
  );

  return (
    <div className="space-y-6">
      {/* Unified Evaluation: Human Test Verdicts + Design Evaluation */}
      {(hasVerdicts || hasDesignerReview) && (
        <div
          className={
            hasVerdicts && hasDesignerReview
              ? "grid grid-cols-1 lg:grid-cols-12 gap-4 items-start"
              : undefined
          }
        >
          {hasVerdicts && verdicts && (
            <HumanTestVerdicts
              verdicts={verdicts}
              className={
                hasDesignerReview
                  ? "lg:col-span-7 rounded-lg border border-border bg-card p-5"
                  : "rounded-lg border border-border bg-card p-5"
              }
              renderReasoning={(text) => (
                <ReasoningText
                  text={text}
                  findings={findingTitles}
                  projectId={projectId}
                  walkthroughId={walkthrough.id}
                  onSelectFinding={onOpenFinding}
                />
              )}
            />
          )}
          {hasDesignerReview && (
            <DesignerReviewSection
              review={walkthrough.designerReview}
              hideDesignCauses
              className={
                hasVerdicts
                  ? "lg:col-span-5 rounded-lg border border-border bg-card p-5"
                  : "rounded-lg border border-border bg-card p-5"
              }
            />
          )}
        </div>
      )}

      {/* Since Last Run */}
      {!isVerification && walkthrough.verification && projectId && (
        <SinceLastRun
          verification={walkthrough.verification as RunVerification}
          projectId={projectId}
          onSelectFinding={onSelectDeltaFinding}
        />
      )}

      {/* Verification Outcomes or Findings List */}
      {isVerification ? (
        <VerificationOutcomesList
          walkthroughId={walkthrough.id}
          steps={walkthroughSteps.map((s) => ({
            id: s.id,
            stepNumber: s.stepNumber,
          }))}
        />
      ) : allFindings.length === 0 ? (
        walkthrough.status === "failed" &&
        walkthroughSteps.length === 0 ? null : (
          <ZeroFindingsState
            status={walkthrough.status}
            personaName={personaName}
          />
        )
      ) : (
        <section className="space-y-4" aria-labelledby="run-findings">
          {/* Segmented Filter Bar */}
          <WorkspaceFindingsToolbar
            problemsCount={problemsCount}
            helpsCount={helpsCount}
            totalFindingsCount={allFindings.length}
            kindFilter={kindFilter}
            onKindFilterChange={onKindFilterChange}
            statusFilter={statusFilter}
            onStatusFilterChange={onStatusFilterChange}
            severityFilter={severityFilter}
            onSeverityFilterChange={onSeverityFilterChange}
            showZenButton={Boolean(
              onOpenZenMode && filteredFindings.length > 0,
            )}
            onOpenZenMode={() => {
              document.documentElement.requestFullscreen?.().catch(() => {});
              onOpenZenMode?.(0);
            }}
          />

          {filteredFindings.length === 0 ? (
            <FilteredFindingsEmptyState
              isComplete={isComplete}
              problemsCount={problemsCount}
              helpsCount={helpsCount}
              openCount={openCount}
              kindFilter={kindFilter}
              statusFilter={statusFilter}
              personaName={personaName}
              onClearFilters={() => {
                onStatusFilterChange("all");
                onSeverityFilterChange("all");
                onKindFilterChange("all");
              }}
              onViewStrengths={() => {
                onKindFilterChange("strength");
                onStatusFilterChange("all");
              }}
              onViewAllAddressed={() => onStatusFilterChange("all")}
            />
          ) : (
            <WorkspaceFindingsList
              filteredFindings={filteredFindings}
              walkthroughSteps={walkthroughSteps}
              selectedIds={selectedIds}
              focusedIndex={focusedIndex}
              allFilteredSelected={allFilteredSelected}
              someFilteredSelected={someFilteredSelected}
              canEdit={canEdit}
              projectId={projectId}
              hasLinkedRepo={hasLinkedRepo}
              onToggleSelectAll={onToggleSelectAll}
              onToggleSelect={onToggleSelect}
              onOpenFinding={onOpenFinding}
              onOpenZenMode={onOpenZenMode}
            />
          )}
        </section>
      )}
    </div>
  );
}
